import { supabase } from './supabase';

/** Control Centre home route and app registry id (see migration control-centre-core-app). */
export const CONTROL_CENTRE_ROUTE = '/admin-dashboard';
export const CONTROL_CENTRE_APP_ID = 'control-centre-core-app';
export const CONTROL_CENTRE_SLUG = 'control-centre';

/** Default when no business home screen is configured in business_apps_v2. */
export const DEFAULT_HOME_ROUTE = CONTROL_CENTRE_ROUTE;

export function isControlCentreApp(app: { id?: string; slug?: string } | null | undefined): boolean {
  if (!app) return false;
  return app.id === CONTROL_CENTRE_APP_ID || app.slug === CONTROL_CENTRE_SLUG;
}

export function isControlCentreRoute(route: string | null | undefined): boolean {
  return route === CONTROL_CENTRE_ROUTE;
}

/** Virtual row for home screen picker when registry row is not yet on business_apps_v2. */
export function getControlCentreHomeScreenOption() {
  return {
    id: 'control-centre-home-option',
    app_id: CONTROL_CENTRE_APP_ID,
    is_home_screen: false,
    app: {
      id: CONTROL_CENTRE_APP_ID,
      name: 'Control Centre',
      slug: CONTROL_CENTRE_SLUG,
      icon: 'LayoutDashboard',
      route_prefix: CONTROL_CENTRE_ROUTE,
    },
  };
}

export function mergeHomeScreenAppOptions(installedApps: Array<{ app_id?: string; app?: { id?: string; slug?: string } }>) {
  if (installedApps.some((row) => isControlCentreApp(row.app) || row.app_id === CONTROL_CENTRE_APP_ID)) {
    return installedApps;
  }
  return [getControlCentreHomeScreenOption(), ...installedApps];
}

/** Resolve redirect route for a business_apps_v2 home screen row. */
export function getHomeScreenRouteFromAppRow(
  row: { app_id?: string; app?: { route_prefix?: string | null } | null } | null | undefined
): string | null {
  if (!row) return null;
  if (row.app_id === CONTROL_CENTRE_APP_ID || isControlCentreApp(row.app as { id?: string; slug?: string })) {
    return CONTROL_CENTRE_ROUTE;
  }
  return row.app?.route_prefix || null;
}

/**
 * Set the business default home screen app.
 * Control Centre may not have a business_apps_v2 row until first selected — insert when needed.
 */
async function setBusinessHomeScreenDirect(
  businessId: string,
  appId: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error: clearError } = await supabase
    .from('business_apps_v2')
    .update({ is_home_screen: false })
    .eq('business_id', businessId);

  if (clearError) {
    return { ok: false, error: clearError.message };
  }

  if (appId === CONTROL_CENTRE_APP_ID) {
    const { error: upsertError } = await supabase.from('business_apps_v2').upsert(
      {
        business_id: businessId,
        app_id: CONTROL_CENTRE_APP_ID,
        status: 'active',
        is_home_screen: true,
      },
      { onConflict: 'business_id,app_id' }
    );

    if (upsertError) {
      return { ok: false, error: upsertError.message };
    }
    return { ok: true };
  }

  const { error } = await supabase
    .from('business_apps_v2')
    .update({ is_home_screen: true })
    .eq('business_id', businessId)
    .eq('app_id', appId);

  if (error) {
    return { ok: false, error: error.message };
  }

  return { ok: true };
}

export async function setBusinessHomeScreen(
  businessId: string,
  appId: string
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { data, error } = await supabase.rpc('set_business_home_screen_app', {
    p_business_id: businessId,
    p_app_id: appId,
  });

  if (error) {
    const missingRpc =
      error.code === 'PGRST202' ||
      error.message?.includes('set_business_home_screen_app') ||
      error.message?.includes('Could not find the function');
    if (missingRpc) {
      return setBusinessHomeScreenDirect(businessId, appId);
    }
    return { ok: false, error: error.message };
  }

  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok === false) {
    return { ok: false, error: result.error || 'Failed to update home screen' };
  }

  return { ok: true };
}
